import { RouterOutput } from '../../../../../../trpc';
import {
  getTeamColor,
  hexWithAlpha,
} from '../../../timeline/components/cross-team-colors';

type CrossTeamResult = RouterOutput['plan']['crossTeamTracksForAthlete'][0];

const CROSS_TEAM_EVENT_BACKGROUND_ALPHA = 0.15;

export type CrossTeamCalendarEvent = {
  id: string;
  title: string;
  start: string;
  allDay: true;
  /** Sorts cross-team events below the active team's events (sortOrder 0). */
  sortOrder: 1;
  /**
   * Never draggable: without this, cross-team events inherit the
   * calendar-wide `editable` (workout drag) and a drop would call
   * moveWorkoutDate against another team's athlete layer.
   */
  startEditable: false;
  extendedProps: {
    kind: 'cross-team';
    primaryColor: string;
    backgroundColor: string;
    teamName: string;
    trackId: string;
    blockId: string;
    workoutId: string;
    sourceTeamId: string;
  };
};

/**
 * Builds FullCalendar events for an athlete's additional-team blocks. Each
 * team gets a deterministic color (by position in `additionalTeamsData`,
 * matching the sidebar's AdditionalTeamsTracks), and every event is labeled
 * with its source team so it reads distinctly from the active team's green
 * events. Cross-team events open the read-only workout preview drawer but
 * aren't editable from the active team's context.
 */
export function buildCrossTeamCalendarEvents(
  additionalTeamsData: readonly CrossTeamResult[],
  selectedCrossTeamTrackIds: ReadonlySet<string>,
  showCanceled: boolean,
): CrossTeamCalendarEvent[] {
  const events: CrossTeamCalendarEvent[] = [];

  additionalTeamsData.forEach((team, index) => {
    const color = getTeamColor(index);
    const backgroundColor = hexWithAlpha(
      color,
      CROSS_TEAM_EVENT_BACKGROUND_ALPHA,
    );

    for (const track of team.tracks) {
      if (!selectedCrossTeamTrackIds.has(track.id)) continue;

      for (const event of track.events) {
        // Mirror the active-team filter in useCalendarSync: canceled workouts
        // are hidden unless the user toggled them on.
        if (!showCanceled && event.status === 'canceled') continue;

        events.push({
          // Prefix: the id is an AthletePublishedWorkoutLayer id that can
          // also surface through the active-team path after a team switch;
          // FullCalendar dedupes events by id.
          id: `cross-team:${event.id}`,
          title: `${team.teamName} • ${event.workoutName}`,
          start: event.assignedDate,
          allDay: true,
          sortOrder: 1,
          startEditable: false,
          extendedProps: {
            kind: 'cross-team',
            primaryColor: color,
            backgroundColor,
            teamName: team.teamName,
            trackId: track.id,
            blockId: event.blockId,
            workoutId: event.workoutId,
            sourceTeamId: team.teamId,
          },
        });
      }
    }
  });

  return events;
}
